import { ROUTES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { format, isBefore, startOfTomorrow } from "date-fns"
import { ReadonlyURLSearchParams, useRouter, useSearchParams } from "next/navigation"
import * as R from "ramda"
import { useEffect } from "react"

import { getQuote } from "../http"
import { Utms } from "../types/global-store"
import { SyncBackBody } from "../types/lead-api-types"
import { computeRedirectRoute } from "../utils/compute-redirect-route"
import { generatePetId, generateUserId } from "../utils/helpers"
import { saveQuoteIdCookie } from "../utils/quote-cookie"
import { storeQuoteId } from "../utils/storage"
import { type Route } from "next"

type SearchParams = { [key: string]: string | string[] | undefined }

const UTM_PARAM_KEYS: (keyof Utms)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_comparateur",
  "gclid",
  "compclid",
  "at_gd",
  "advertiser_id",
  "channel_id",
  "market",
  "click_id",
  "click_ref",
  "s_id",
  "affiliate_gclid",
  "transaction_id",
]

const toSingleValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value

const extractUtmParams = (searchParams: ReadonlyURLSearchParams): Utms => {
  const utms = {} as Utms

  UTM_PARAM_KEYS.forEach((key) => {
    const value = toSingleValue(searchParams.get(key as string))
    if (value) {
      utms[key] = value
    }
  })

  if (!utms.affiliate_gclid) {
    const fallbackAffiliateGclid = toSingleValue(searchParams.get("affiliateGclid"))
    if (fallbackAffiliateGclid) {
      utms.affiliate_gclid = fallbackAffiliateGclid
    }
  }

  return utms
}

const mergeQuoteWithMeta = (
  currentQuote: Partial<SyncBackBody> | undefined,
  quotePatch: Partial<SyncBackBody>,
  utmParams: Utms
): Partial<SyncBackBody> => {
  const existingQuote = currentQuote || {}
  const existingMeta = existingQuote.meta || {}
  const patchMeta = quotePatch.meta || {}

  return {
    ...existingQuote,
    ...quotePatch,
    meta: {
      ...existingMeta,
      ...patchMeta,
      utm: {
        ...utmParams,
        ...(patchMeta.utm || {}),
        ...(existingMeta.utm || {}),
      },
    },
  }
}

const ensureFutureDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  const tomorrow = startOfTomorrow()
  return format(isBefore(date, tomorrow) ? tomorrow : date, "yyyy-MM-dd")
}

export const formatQuoteResponse = (
  quoteResponse: Awaited<ReturnType<typeof getQuote>>,
  utmParams?: Utms,
  searchParams?: ReadonlyURLSearchParams
): Partial<SyncBackBody> => ({
  // Sort by pet_idx — the backend does not guarantee insertion order, and
  // downstream UI (tribe, tribeQuote cards) renders pets in array order.
  pets: [...quoteResponse.data]
    .sort((a, b) => (a.pet_idx ?? 0) - (b.pet_idx ?? 0))
    .map((pet) => ({
      name: pet.pet_name,
      created_at: ensureFutureDate(pet.start_date),
      id: generatePetId(),
      idx: pet.pet_idx,
      type: pet.pet_type as "cat" | "dog",
      sexe: pet.pet_sexe as "MALE" | "FEMALE",
      birthday: pet.pet_birthday,
      price: (pet.price as number) || null,
      breed: { race: pet.pet_race },
    })),
  owner: {
    // "_" is the backend placeholder when a landing page created the quote
    // without asking for an email. Treat it as null so syncBack doesn't
    // send an invalid email back to the server.
    email:
      quoteResponse.pet_owner.owner_email && quoteResponse.pet_owner.owner_email !== "_"
        ? quoteResponse.pet_owner.owner_email
        : null,
    phone: quoteResponse.pet_owner.owner_phone || "+33600000000",
  },
  meta: {
    quote_id: quoteResponse.data[0].quote_id,
    user_id: quoteResponse.data[0].user_id || generateUserId(),
    pricing_version: quoteResponse.data[0].pricing_version,
    start_date: ensureFutureDate(quoteResponse.data[0].start_date),
    subscription_mode: quoteResponse.data[0].subscription_mode as "ASAP" | "DEFERRED",
    raf_code: quoteResponse.data[0].raf_code,
    promo_code:
      quoteResponse.data[0].promo_code || toSingleValue(searchParams?.get("promo_code")) || null,
    utm: {
      ...utmParams,
      ...Object.fromEntries(
        Object.entries(quoteResponse.data?.[0]?.utm_params || {}).filter(([, v]) => Boolean(v))
      ),
    },
  },
  marketing: {
    ...(quoteResponse.data?.[0]?.marketing || {}),
  },
})

export function useQuoteFetching(searchParams: SearchParams, isFromHealth = false): null {
  const [state, send] = useActor(newSubscriptionFlowService)
  const router = useRouter()
  const newSearchParams = useSearchParams()

  useEffect(() => {
    const utmParams = extractUtmParams(newSearchParams)
    const petName = toSingleValue(newSearchParams.get("petName"))
    const quoteId = toSingleValue(newSearchParams.get("quote_id"))
    const promoCode = toSingleValue(newSearchParams.get("promo_code"))
    const updateQuoteContext = (quotePatch: Partial<SyncBackBody>) => {
      send({
        type: "UPDATE_QUOTE",
        quote: mergeQuoteWithMeta(state.context.quote, quotePatch, utmParams),
      })
    }

    if (!quoteId) {
      if (petName) {
        const newPetId = generatePetId()
        updateQuoteContext({
          pets: [
            {
              name: petName,
              idx: 0,
              id: newPetId,
            },
          ],
          meta: R.reject(R.isNil, {
            promo_code: promoCode || undefined,
          }),
        })
        send({
          type: "SET_CURRENT_PET_IDX",
          petId: newPetId,
          petIndex: 0,
        })
        router.push(ROUTES.petInfo.path)
        return
      }

      updateQuoteContext({
        meta: R.reject(R.isNil, {
          promo_code: promoCode || undefined,
        }),
      })
      router.push(ROUTES.petName.path)
      return
    }

    const fetchQuote = async () => {
      try {
        const quote = await getQuote(quoteId)
        const formattedQuote = formatQuoteResponse(quote, utmParams, newSearchParams)
        storeQuoteId(quoteId)
        saveQuoteIdCookie(quoteId)
        send({
          type: "UPDATE_QUOTE",
          quote: formattedQuote,
        })

        if (isFromHealth) {
          const [firstPet] = formattedQuote.pets || []
          if (firstPet) {
            send({
              type: "SET_CURRENT_PET_IDX",
              petId: firstPet.id,
              petIndex: firstPet.idx,
            })
            router.push(ROUTES.petClaimHistory.path)
            return
          }
          router.push(ROUTES.petName.path)
          return
        }

        const [firstPet] = formattedQuote.pets || []
        if (firstPet) {
          send({
            type: "SET_CURRENT_PET_IDX",
            petId: firstPet.id,
            petIndex: firstPet.idx,
          })
        }
        router.push(computeRedirectRoute(quote) as Route)
      } catch {
        updateQuoteContext({})
        router.push(ROUTES.petName.path)
      }
    }

    void fetchQuote()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot mount fetch; adding state/router/send/searchParams would re-fire redirects
  }, [])

  return null
}
